import {
  AspectRatio,
  Box,
  Button,
  Card,
  Container,
  Flex,
  Heading,
  Image,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useGetNewsMediaPublicationQuery } from "../../features/cmsApi";
import BreadcrumbComponent from "./BreadcrumbComponent";

interface MediaItem {
  id: string | number;
  title: string;
  videoLink: string;
  uploadedDate: string;
}

const Media = () => {
  const { data } = useGetNewsMediaPublicationQuery();
  const [media, setMedia] = useState<MediaItem[]>([]);
  const [selectedVideo, setSelectedVideo] = useState<MediaItem | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  

  // Update media state when data is received
  useEffect(() => {
    if (data?.data?.media) {
      setMedia(data.data.media);
    }
  }, [data]);

  // Function to get YouTube video thumbnail
  const getYouTubeThumbnail = (url: string) => {
    const videoId = url.split("v=")[1]?.split("&")[0];
    return videoId
      ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
      : null;
  };

  // Function to get embeddable YouTube URL
  const getEmbedUrl = (url: string) => {
    // console.log("Original URL:", url);
    const videoId = url.split("v=")[1]?.split("&")[0];
    // console.log("Extracted Video ID:", videoId);
    return videoId
      ? `https://www.youtube.com/embed/${videoId}?autoplay=1`
      : url;
  };

  const handleVideoPlay = (item: MediaItem) => {
    setSelectedVideo(item);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedVideo(null);
  };

  return (
    <Container maxW="7xl" py={12}>
      {/* Header Section */}
      <Box textAlign="center">
        <VStack align="center">
          <Flex justify="space-between" align="center" w="full">
            <BreadcrumbComponent
              data={[
                { title: "Home", href: "/" },
                { title: "Media", href: null },
              ]}
            />
          </Flex>
          <Box textAlign="center" mb={{ base: 4, md: 4 }}>
            <Text
              fontSize={{ base: "2xl", sm: "3xl", md: "4xl", lg: "5xl" }}
              fontWeight="bold"
              color="#090047"
              fontFamily="Cinema Sunday"
            >
              Media
            </Text>
          </Box>
        </VStack>
      </Box>

      {/* Media Grid */}
      <SimpleGrid columns={[1, 1, 2, 3]} gap={8}>
        {media.map((item) => (
          <Card.Root
            key={item.id}
            overflow="hidden"
            transition="all 0.3s ease"
            _hover={{
              transform: "translateY(-8px)",
              shadow: "xl",
              borderColor: "red.200",
            }}
            bg="white"
            borderWidth="1px"
            borderColor="gray.200"
            borderRadius="xl"
            cursor="pointer"
            onClick={() => handleVideoPlay(item)}
          >
            {/* Video Thumbnail */}
            <Card.Header p={0} position="relative">
              <AspectRatio ratio={16 / 9}>
                <Image
                  src={
                    getYouTubeThumbnail(item.videoLink) ||
                    "/api/placeholder/400/225"
                  }
                  alt={item.title}
                  objectFit="cover"
                  w="100%"
                  h="100%"
                  borderTopRadius="xl"
                />
              </AspectRatio>

              {/* Blinking Play Button Overlay */}
              <Box
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
                bg="rgba(0, 0, 0, 0.7)"
                borderRadius="full"
                p={4}
                display="flex"
                alignItems="center"
                justifyContent="center"
                transition="all 0.3s ease"
                _hover={{
                  bg: "rgba(0, 0, 0, 0.9)",
                  transform: "translate(-50%, -50%) scale(1.1)",
                }}
                css={{
                  "@keyframes blink": {
                    "0%, 50%": { opacity: 1 },
                    "51%, 100%": { opacity: 0.5 },
                  },
                  animation: "blink 2s infinite",
                }}
              >
                <Box
                  w="0"
                  h="0"
                  borderStyle="solid"
                  borderWidth="15px 0 15px 25px"
                  borderColor="transparent transparent transparent white"
                  ml="5px"
                />
              </Box>

              {/* Upload Date Badge */}
              <Box
                position="absolute"
                top={4}
                right={4}
                bg="rgba(0, 0, 0, 0.8)"
                color="white"
                px={3}
                py={1}
                borderRadius="full"
                fontSize="xs"
                fontWeight="semibold"
              >
                {new Date(item.uploadedDate).toLocaleDateString()}
              </Box>
            </Card.Header>

            {/* Video Title */}
            <Card.Body p={6}>
              <Heading
                size="md"
                lineHeight="1.3"
                color="gray.800"
                textAlign="center"
                _hover={{ color: "#293354" }}
                transition="color 0.3s ease"
              >
                {item.title}
              </Heading>
            </Card.Body>
          </Card.Root>
        ))}
      </SimpleGrid>

      {/* Custom Modal for Video */}
      {isModalOpen && selectedVideo && (
        <Box
          position="fixed"
          top="0"
          left="0"
          w="100vw"
          h="100vh"
          bg="rgba(0, 0, 0, 0.8)"
          zIndex="modal"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={closeModal}
        >
          <Box
            position="relative"
            w={{ base: "95%", md: "85%", lg: "70%" }}
            maxW="900px"
            bg="white"
            borderRadius="xl"
            overflow="hidden"
            boxShadow="2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <Button
              position="absolute"
              top={4}
              right={4}
              zIndex="tooltip"
              size="sm"
              variant="solid"
              bg="rgba(0, 0, 0, 0.7)"
              color="white"
              borderRadius="full"
              _hover={{ bg: "rgba(0, 0, 0, 0.9)" }}
              onClick={closeModal}
            >
              ✕
            </Button>

            {/* Video Title */}
            <Box p={6} pb={3}>
              <Heading size="lg" color="gray.800" textAlign="center">
                {selectedVideo.title}
              </Heading>
            </Box>

            {/* Video Player */}
            <Box p={6} pt={0}>
              <AspectRatio ratio={16 / 9}>
                <iframe
                  src={getEmbedUrl(selectedVideo.videoLink)}
                  allowFullScreen
                  style={{
                    border: 0,
                    width: "100%",
                    height: "100%",
                    borderRadius: "8px",
                  }}
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              </AspectRatio>
            </Box>
          </Box>
        </Box>
      )}

      {/* No Media Available */}
      {media.length === 0 && (
        <Box py={16} textAlign="center">
          <Text fontSize="6xl" opacity={0.3} mb={6}>
            🎥
          </Text>
          <Heading size="lg" color="gray.600" mb={4}>
            No Media Available
          </Heading>
          <Text color="gray.500">Check back later for the latest videos!</Text>
        </Box>
      )}
    </Container>
  );
};

export default Media;
